import { Box, Tooltip } from "@chakra-ui/react";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { CallOrDistributionEnum } from "gen/model/callOrDistributionEnum";
import { FeeBasisDeterminationEnum } from "gen/model/feeBasisDeterminationEnum";
import { FeeCadenceEnum } from "gen/model/feeCadenceEnum";
import { FeeChargeTimingEnum } from "gen/model/feeChargeTimingEnum";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { FeeDayCountEnum } from "gen/model/feeDayCountEnum";
import { FeePartialPeriodBasisDeterminationEnum } from "gen/model/feePartialPeriodBasisDeterminationEnum";
import { FeeTypeEnum } from "gen/model/feeTypeEnum";
import { FundEntityData } from "gen/model/fundEntityData";
import { SubClassificationEnum } from "gen/model/subClassificationEnum";
import { TransactionCodeConfigListClassification } from "gen/model/transactionCodeConfigListClassification";
import { TransactionTypeEnum } from "gen/model/transactionTypeEnum";
import React, { useMemo } from "react";
import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormSelect } from "shared/components/input/select/Select";
import {
  SelectElement,
  SelectOption,
} from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { enumKeys } from "shared/utils/EnumUtils";

interface FeeFormSectionProps {
  control: Control<
    FeeCreateAPIDataRequest & {
      fee_eligible_for_true_up: boolean;
    }
  >;
  fundFamilyId: string;
  fundEntitiesData: FundEntityData[];
  isDisabled?: boolean;
}

const CadenceFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const cadenceElements = enumKeys(FeeCadenceEnum).map<SelectElement>(
    (cadence) => ({
      value: cadence,
      rich_value: t(`enums:FeeCadenceEnum.${cadence}`),
    }),
  );

  return (
    <FormSelect
      name="fee.cadence"
      title={t("fees:cadence")}
      elements={cadenceElements}
      isDisabled={isDisabled}
    />
  );
};

const DayCountFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const dayCountElements = enumKeys(FeeDayCountEnum).map<SelectElement>(
    (day_count) => ({
      value: day_count,
      rich_value: t(`enums:DayCountEnum.${day_count}`),
    }),
  );

  return (
    <FormSelect
      name="fee.day_count"
      title={t("fees:dayCount")}
      elements={dayCountElements}
      isDisabled={isDisabled}
    />
  );
};

const PartialPeriodDayCountFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const dayCountElements = enumKeys(FeeDayCountEnum).map<SelectElement>(
    (day_count) => ({
      value: day_count,
      rich_value: t(`enums:DayCountEnum.${day_count}`),
    }),
  );

  return (
    <FormSelect
      name="fee.partial_period_day_count"
      title={t("fees:partialPeriodDayCount")}
      elements={dayCountElements}
      isDisabled={isDisabled}
    />
  );
};

const FeeChargeTimingFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);
  return (
    <FormToggleButtonGroup
      name="fee.charge_timing"
      title={t("fees:chargeTiming.title")}
      isDisabled={isDisabled}
    >
      <ToggleButton value={FeeChargeTimingEnum.in_advance}>
        {t("enums:ChargeTimingEnum.in_advance")}
      </ToggleButton>
      <ToggleButton value={FeeChargeTimingEnum.in_arrears}>
        {t("enums:ChargeTimingEnum.in_arrears")}
      </ToggleButton>
    </FormToggleButtonGroup>
  );
};

const FeeBasisDeterminationFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const feeBasisTypeElements = enumKeys(
    FeeBasisDeterminationEnum,
  ).map<SelectElement>((feeBasisType) => ({
    value: feeBasisType,
    rich_value: t(`enums:FeeBasisDeterminationEnum.${feeBasisType}`),
  }));

  return (
    <FormSelect
      name="fee.fee_basis_determination"
      title={t("fees:feeBasis")}
      elements={feeBasisTypeElements}
      isDisabled={isDisabled}
    />
  );
};

const FeePartialPeriodBasisDeterminationFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const feeBasisTypeElements = enumKeys(
    FeePartialPeriodBasisDeterminationEnum,
  ).map<SelectElement>((feeBasisType) => ({
    value: feeBasisType,
    rich_value: t(
      `enums:FeePartialPeriodBasisDeterminationEnum.${feeBasisType}`,
    ),
  }));

  return (
    <FormSelect
      name="fee.partial_period_basis_determination"
      title={t("fees:feeBasisPartialPeriod")}
      elements={feeBasisTypeElements}
      isDisabled={isDisabled}
    />
  );
};

const FeeTrueUpMethodologyFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const feeBasisTypeElements = enumKeys(
    FeeBasisDeterminationEnum,
  ).map<SelectElement>((feeBasisType) => ({
    value: feeBasisType,
    rich_value: t(`enums:FeeBasisDeterminationEnum.${feeBasisType}`),
  }));

  const eligibleForTrueUp = useWatch({
    name: "fee_eligible_for_true_up",
  }) as boolean;

  return (
    <>
      <FormCheckbox
        name="fee_eligible_for_true_up"
        isDisabled={isDisabled}
        marginBottom="8px"
      >
        {t("fees:feeForm.feeEligibleForTrueUp")}
        <Tooltip
          key="tooltip"
          label={t("fees:feeForm.feeEligibleForTrueUpTooltip")}
          placement="top"
        >
          <InfoCircleIcon w="12px" h="12px" ml="10px" color="gray.400" />
        </Tooltip>
      </FormCheckbox>
      {eligibleForTrueUp && (
        <FormSelect
          name="fee.true_up_methodology"
          title={t("fees:trueUpMethodology")}
          elements={feeBasisTypeElements}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
};

const FeeTypeFormSelect: React.FC<{
  isDisabled?: boolean;
}> = ({ isDisabled }) => {
  const { t } = useTranslation(["fees", "enums"]);

  const feeTypeElements = enumKeys(FeeTypeEnum).map<SelectElement>(
    (feeType) => ({
      value: feeType,
      rich_value: t(`enums:FeeTypeEnum.${feeType}`),
    }),
  );

  return (
    <FormSelect
      name="fee.fee_type"
      title={t("fees:feeType")}
      elements={feeTypeElements}
      isDisabled={isDisabled}
    />
  );
};

const TransactionCodeFormSelect: React.FC<{
  name: string;
  title: string;
  isDisabled?: boolean;
  fundFamilyId: string;
  transactionType?: TransactionTypeEnum;
  callOrDistribution?: CallOrDistributionEnum;
  classification?: TransactionCodeConfigListClassification;
  subClassification?: SubClassificationEnum;
}> = ({
  name,
  title,
  isDisabled,
  fundFamilyId,
  transactionType,
  callOrDistribution,
  classification,
  subClassification,
}) => {
  const { data: transactionCodeData } = useTransactionCodeConfigListSuspense(
    fundFamilyId,
    {
      transaction_type: transactionType,
      call_or_distribution: callOrDistribution,
      classification,
      sub_classification: subClassification,
    },
  );

  const elements = useMemo(
    () =>
      transactionCodeData.map(({ transaction_code_id, rich_name }) => ({
        value: transaction_code_id,
        rich_value: rich_name!,
      })),
    [transactionCodeData],
  );

  return (
    <FormSelect
      name={name}
      title={title}
      elements={elements}
      isDisabled={isDisabled}
    />
  );
};

const FeeFormSectionBasics: React.FC<FeeFormSectionProps> = ({
  fundFamilyId,
  fundEntitiesData,
  isDisabled,
}) => {
  const { t } = useTranslation(["fees"]);
  const appliesToAllFunds = useWatch({
    name: "applies_to_all_funds",
  }) as boolean;

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>{t("fees:feeForm.title")}</ContentBlockTitle>
      </ContentBlockHeader>
      <ContentBlockBody variant="form">
        <FormTextInput
          isDisabled={isDisabled}
          name="fee.name"
          title={t("fees:feeName")}
        />
        <CadenceFormSelect isDisabled={isDisabled} />
        <DayCountFormSelect isDisabled={isDisabled} />
        <PartialPeriodDayCountFormSelect isDisabled={isDisabled} />
        <FeeTypeFormSelect isDisabled={isDisabled} />
        <FeeChargeTimingFormSelect isDisabled={isDisabled} />
        <FeeBasisDeterminationFormSelect isDisabled={isDisabled} />
        <FeePartialPeriodBasisDeterminationFormSelect isDisabled={isDisabled} />
        <FeeTrueUpMethodologyFormSelect isDisabled={isDisabled} />
        <TransactionCodeFormSelect
          name="fee.call_transaction_code_id"
          title={t("fees:transactionCodeCall")}
          isDisabled={isDisabled}
          fundFamilyId={fundFamilyId}
          transactionType="capital"
          callOrDistribution="call"
          classification="management_fee"
        />
        {/* Disabling setting fund level fees for the time being until we clean up dashboards (Only GTIS has this).*/}
        <FormCheckbox name="applies_to_all_funds" isDisabled={true}>
          {t("fees:feeForm.appliesToAllFunds")}
        </FormCheckbox>
        {!appliesToAllFunds && fundEntitiesData.length > 0 && (
          <Box marginTop="20px">
            <FormSelect
              isMulti={true}
              name="fund_ids"
              title={t("fees:selectFunds")}
              isDisabled={true}
            >
              {fundEntitiesData.map((fundEntity) => (
                <SelectOption key={fundEntity.id} value={fundEntity.id!}>
                  {fundEntity.legal_name}
                </SelectOption>
              ))}
            </FormSelect>
          </Box>
        )}
      </ContentBlockBody>
    </ContentBlock>
  );
};

export default FeeFormSectionBasics;
